import {DateTime} from 'luxon';
import {ExportColumnId, UnitPlacement} from '../shared/models/export-columns.model';
import {
    DECIMAL_PRECISION_AS_STORED,
    exportDateFormatPatterns,
    ExportFileFormat,
    exportTimestampShape,
} from '../shared/models/export-file-format.model';

export type FilePreviewColumn = {id: ExportColumnId; header: string};

export type FilePreviewSample = Record<ExportColumnId, string>;

export type ExportedGridInput = {
    columns: ReadonlyArray<FilePreviewColumn>;
    unitPlacement: UnitPlacement;
    samples: ReadonlyArray<FilePreviewSample>;
    fileFormat: ExportFileFormat;
};

export type ExportedCell = {text: string; numeric: boolean};

export type ExportedValueRole = 'text' | 'number' | 'date';

export type ExportedValue = {
    text: string;
    cell: ExportedCell;
    role: ExportedValueRole;
};

export type ExportedGrid = {
    headers: ReadonlyArray<ExportedValue>;
    rows: ReadonlyArray<ReadonlyArray<ExportedValue>>;
};

export const sampleTextColumns: ReadonlyArray<ExportColumnId> = [
    'street',
    'placement',
    'usage',
    'type',
];

export const sampleMeters: ReadonlyArray<FilePreviewSample> = [
    {
        systemNo: '99999',
        serialNo: '10000001',
        printedSerialNo: '00000001',
        propertyNo: '001',
        branchNo: '099',
        locationNo: '001',
        street: 'customizedexports_export_preview_sample_street',
        stairway: '1',
        floor: 'st',
        placement: 'customizedexports_export_preview_sample_kitchen',
        usage: 'customizedexports_export_preview_sample_hot_water',
        type: 'customizedexports_export_preview_sample_water',
        latestReading: '2026-04-18T10:30:00+02:00',
        meterValue: '1234.56',
        unit: 'm³',
        installationDate: '2019-03-12',
        dismountedDate: '',
    },
    {
        systemNo: '99999',
        serialNo: '10000002',
        printedSerialNo: '00000002',
        propertyNo: '001',
        branchNo: '099',
        locationNo: '001',
        street: 'customizedexports_export_preview_sample_street',
        stairway: '1',
        floor: 'st',
        placement: 'customizedexports_export_preview_sample_living_room',
        usage: 'customizedexports_export_preview_sample_heating',
        type: 'customizedexports_export_preview_sample_heat',
        latestReading: '2026-04-19T07:05:00+02:00',
        meterValue: '2345.60',
        unit: 'GJ',
        installationDate: '2020-11-02',
        dismountedDate: '',
    },
    {
        systemNo: '99999',
        serialNo: '10000003',
        printedSerialNo: '00000003',
        propertyNo: '001',
        branchNo: '099',
        locationNo: '002',
        street: 'customizedexports_export_preview_sample_street',
        stairway: '',
        floor: '1',
        placement: 'customizedexports_export_preview_sample_bathroom',
        usage: 'customizedexports_export_preview_sample_cold_water',
        type: 'customizedexports_export_preview_sample_water',
        latestReading: '2026-04-20T22:41:00+02:00',
        meterValue: '345.70',
        unit: 'm³',
        installationDate: '2021-06-30',
        dismountedDate: '',
    },
    {
        systemNo: '99999',
        serialNo: '10000004',
        printedSerialNo: '00000004',
        propertyNo: '001',
        branchNo: '099',
        locationNo: '002',
        street: 'customizedexports_export_preview_sample_street',
        stairway: '',
        floor: '1',
        placement: 'customizedexports_export_preview_sample_bathroom',
        usage: 'customizedexports_export_preview_sample_cold_water',
        type: 'customizedexports_export_preview_sample_water',
        latestReading: '2026-02-04T09:12:00+01:00',
        meterValue: '456.78',
        unit: 'm³',
        installationDate: '2018-09-17',
        dismountedDate: '2026-02-05',
    },
];

export type FormattedValueKind = 'timestamp' | 'date' | 'number';

const formattedColumns: Partial<Record<ExportColumnId, FormattedValueKind>> = {
    latestReading: 'timestamp',
    installationDate: 'date',
    dismountedDate: 'date',
    meterValue: 'number',
};

export function formattedValueKind(id: ExportColumnId): FormattedValueKind | null {
    return formattedColumns[id] ?? null;
}

const kotlinDoubleSuffix = /[DdFf]/;

const maxNumberCellLength = 16;

function numberOrNull(value: string): number | null {
    if (
        value.trim() === '' ||
        kotlinDoubleSuffix.test(value) ||
        value.length >= maxNumberCellLength
    ) {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function periodForm(kind: FormattedValueKind, value: string, fileFormat: ExportFileFormat): string {
    switch (kind) {
        case 'timestamp': {
            const {pattern, inUtc} = exportTimestampShape(fileFormat);
            const read = DateTime.fromISO(value, {setZone: true});
            return (inUtc ? read.toUTC() : read).toFormat(pattern);
        }
        case 'date':
            return DateTime.fromISO(value).toFormat(
                exportDateFormatPatterns[fileFormat.dateFormat],
            );
        case 'number':
            return fileFormat.decimalPrecision === DECIMAL_PRECISION_AS_STORED
                ? value
                : cutToDecimals(value, fileFormat.decimalPrecision);
    }
}

function cutToDecimals(value: string, decimals: number): string {
    const [whole, fraction = ''] = value.split('.');
    if (decimals === 0) {
        return whole;
    }
    return `${whole}.${fraction.slice(0, decimals).padEnd(decimals, '0')}`;
}

function withDecimalSeparator(digits: string, fileFormat: ExportFileFormat): string {
    return fileFormat.decimalSeparator === 'COMMA' ? digits.replace('.', ',') : digits;
}

function valueRole(kind: FormattedValueKind | null, asNumber: number | null): ExportedValueRole {
    if (kind === 'date' || kind === 'timestamp') {
        return 'date';
    }
    return asNumber === null ? 'text' : 'number';
}

function exportedValue(
    column: FilePreviewColumn,
    sample: FilePreviewSample,
    {unitPlacement, fileFormat}: ExportedGridInput,
): ExportedValue {
    const stored = sample[column.id];
    const kind = formattedValueKind(column.id);
    const period = kind === null || stored === '' ? stored : periodForm(kind, stored, fileFormat);
    const text = kind === 'number' ? withDecimalSeparator(period, fileFormat) : period;
    if (column.id === 'meterValue' && unitPlacement === 'FOLDED_INTO_VALUE') {
        const withUnit = `${text} ${sample.unit}`;
        return {
            text: withUnit,
            cell: {text: withUnit, numeric: false},
            role: 'text',
        };
    }
    const asNumber = numberOrNull(period);
    return {
        text,
        cell:
            asNumber === null
                ? {text, numeric: false}
                : {text: withDecimalSeparator(String(asNumber), fileFormat), numeric: true},
        role: valueRole(kind, asNumber),
    };
}

export function exportedGrid(input: ExportedGridInput): ExportedGrid {
    return {
        headers: input.columns.map(column => ({
            text: column.header,
            cell: {text: column.header, numeric: false},
            role: 'text' as const,
        })),
        rows: input.samples.map(sample =>
            input.columns.map(column => exportedValue(column, sample, input)),
        ),
    };
}

const exampleInstant = '2026-04-20T14:30:00+02:00';

export function exportedTimestampExample(fileFormat: ExportFileFormat): string {
    return periodForm('timestamp', exampleInstant, fileFormat);
}
